import { useState, useCallback, useMemo } from 'react';

import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useButtonPressedCallback, useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import ErrorState from '@zenbusiness/zui/ErrorState';
import PageContainer from '@zenbusiness/zui/PageContainer';
import PageLoading from '@zenbusiness/zui/PageLoading';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { ChevronLeftRoundedIcon, CloseIcon, getSubscriptionRestartPaths } from './constants';
import { getRestartMessages } from './messages';
import RestartConfirmStep from './RestartConfirmStep';
import {
  buildRestartSubscriptionContext,
  getRestartClickstreamProps,
  RESTART_CHANGE_TYPE,
  RESTART_PHASES,
} from './restartFlowConfig';
import RestartLoadingStep from './RestartLoadingStep';
import RestartResultStep from './RestartResultStep';

import { ENABLE_DASHBOARD_SUBSCRIPTION_RESTART } from '../../constants/experimentsFlags';
import { useSubscriptionRestart } from '../../hooks/useSubscriptionRestart';
import { isCustomerActionAllowed } from '../Subscriptions/utils/selfServiceDnrEligibility';

export default function SubscriptionRestartPage() {
  const { businessEntityUuid, orderBillingItemUuid } = useParams();
  const navigate = useNavigate();

  const [restartPhase, setRestartPhase] = useState(RESTART_PHASES.CONFIRM);
  const [restartSuccess, setRestartSuccess] = useState(null);

  const subscriptionRestartEnabled = useExperimentFlag(ENABLE_DASHBOARD_SUBSCRIPTION_RESTART, null);
  const restartFlagLoading = subscriptionRestartEnabled === null;

  const { subscription, paymentMethod, loading, hasError, submitChange } = useSubscriptionRestart();

  const paths = useMemo(() => getSubscriptionRestartPaths(businessEntityUuid), [businessEntityUuid]);

  const canRestartSubscription =
    subscriptionRestartEnabled === true && isCustomerActionAllowed(subscription, 'undoDoNotRenew');
  const canResumeTrial = isCustomerActionAllowed(subscription, 'resumeTrial');

  const subscriptionContext = buildRestartSubscriptionContext(subscription);

  const trackBack = useButtonPressedCallback(getRestartClickstreamProps('back', subscriptionContext));
  const trackClose = useButtonPressedCallback(getRestartClickstreamProps('close', subscriptionContext));
  const trackRenewSubmit = useButtonPressedCallback(getRestartClickstreamProps('submit', subscriptionContext));
  const trackSuccess = useButtonPressedCallback(getRestartClickstreamProps('success', subscriptionContext));
  const trackError = useClickstreamCallback('errorDisplayed', getRestartClickstreamProps('error', subscriptionContext));
  const trackReturnDashboard = useButtonPressedCallback(
    getRestartClickstreamProps('returnDashboard', subscriptionContext),
  );
  const trackReturnSubscriptions = useButtonPressedCallback(
    getRestartClickstreamProps('returnSubscriptions', subscriptionContext),
  );
  const trackContactUs = useButtonPressedCallback(getRestartClickstreamProps('contactUs', subscriptionContext));

  const handleBack = useCallback(() => {
    trackBack();
    navigate(-1);
  }, [navigate, trackBack]);

  const handleClose = useCallback(() => {
    trackClose();
    navigate(paths.subscriptions);
  }, [navigate, paths.subscriptions, trackClose]);

  const handleSubmit = useCallback(
    async (event) => {
      trackRenewSubmit(event);
      setRestartPhase(RESTART_PHASES.LOADING);
      try {
        const result = await submitChange({
          variables: {
            input: {
              orderBillingItemUuid,
              changeType: RESTART_CHANGE_TYPE,
            },
          },
        });
        const statusCode = result?.data?.billingSubscriptionChange?.statusCode;
        const success = Boolean(statusCode && statusCode >= 200 && statusCode < 300);
        if (success) {
          trackSuccess();
        } else {
          trackError();
        }
        setRestartSuccess(success);
      } catch {
        trackError();
        setRestartSuccess(false);
      } finally {
        setRestartPhase(RESTART_PHASES.RESULT);
      }
    },
    [submitChange, orderBillingItemUuid, trackRenewSubmit, trackSuccess, trackError],
  );

  const handleReturnDashboard = useCallback(() => {
    trackReturnDashboard();
    navigate(paths.dashboard);
  }, [navigate, paths.dashboard, trackReturnDashboard]);

  const handleReturnSubscriptions = useCallback(() => {
    trackReturnSubscriptions();
    navigate(paths.subscriptions);
  }, [navigate, paths.subscriptions, trackReturnSubscriptions]);

  const handleContactUs = useCallback(() => {
    trackContactUs();
    navigate(paths.help);
  }, [navigate, paths.help, trackContactUs]);

  if (loading) {
    return <PageLoading />;
  }

  if (hasError) {
    return (
      <PageContainer>
        <ErrorState
          title={<FormattedMessage id="dashboard.subscriptionRestart.error.loading.title" />}
          description={<FormattedMessage id="dashboard.subscriptionRestart.error.loading.message" />}
        />
      </PageContainer>
    );
  }

  if (!subscription) {
    return <Navigate to={paths.subscriptions} replace />;
  }

  // Wait for LD to resolve before redirecting paid subscriptions (trials do not need the flag).
  if (!subscription.inTrial && restartFlagLoading) {
    return <PageLoading />;
  }

  const paidRestartMissingSummaryDate = !subscription.inTrial && !subscription.termEndDate;

  if ((subscription.inTrial ? !canResumeTrial : !canRestartSubscription) || paidRestartMissingSummaryDate) {
    return <Navigate to={paths.subscriptions} replace />;
  }

  return (
    <Box bgcolor="background.default" minHeight="100vh">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        py={1.5}
        px={2}
        bgcolor="background.default"
      >
        <Button variant="text" onClick={handleBack} startIcon={<ChevronLeftRoundedIcon />}>
          <FormattedMessage id="dashboard.subscriptionRestart.header.back" />
        </Button>
        <Text size="xl" weight="bold">
          <FormattedMessage id={getRestartMessages(subscription.inTrial).pageTitle} />
        </Text>
        <Button variant="text" onClick={handleClose} endIcon={<CloseIcon fontSize="small" />}>
          <FormattedMessage id="dashboard.subscriptionRestart.header.close" />
        </Button>
      </Stack>

      <Stack gap={3} maxWidth={600} mx="auto" py={5} px={3}>
        {restartPhase === RESTART_PHASES.LOADING && <RestartLoadingStep />}
        {restartPhase === RESTART_PHASES.RESULT && (
          <RestartResultStep
            success={restartSuccess}
            onReturnDashboard={handleReturnDashboard}
            onReturnSubscriptions={handleReturnSubscriptions}
            onContactUs={handleContactUs}
          />
        )}
        {restartPhase === RESTART_PHASES.CONFIRM && (
          <RestartConfirmStep
            subscription={subscription}
            paymentMethod={paymentMethod}
            onSubmit={handleSubmit}
            submitting={false}
          />
        )}
      </Stack>
    </Box>
  );
}
